// Passkey (WebAuthn) frontend helper.
// Uses @simplewebauthn/browser to run the ceremony and talks to the
// FastAPI portal_passkey routes. The browser lib handles all base64url
// conversions — pass the server JSON directly as `optionsJSON`.
import axios from "axios";
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

// CSRF interceptor — mirrors PortalShell's portalApi so we can share it here
// even before the shell mounts (e.g. from AffiliateLogin).
client.interceptors.request.use((cfg) => {
  const m = (cfg.method || "get").toUpperCase();
  if (["POST", "PATCH", "PUT", "DELETE"].includes(m)) {
    const tok = (document.cookie || "")
      .split(";")
      .map((s) => s.trim())
      .find((s) => s.startsWith("csrf_token="));
    if (tok) cfg.headers["X-CSRF-Token"] = decodeURIComponent(tok.split("=")[1]);
  }
  return cfg;
});

export function isPasskeySupported() {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

export async function isPlatformAuthenticatorAvailable() {
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

export async function addPasskey(nickname) {
  const { data: optionsJSON } = await client.post("/affiliate/auth/passkey/register/begin");
  const attResp = await startRegistration({ optionsJSON });
  const { data } = await client.post("/affiliate/auth/passkey/register/finish", {
    credential: attResp,
    nickname: nickname || "Passkey",
  });
  return data;
}

export async function signInWithPasskey() {
  const { data: optionsJSON } = await client.post("/affiliate/auth/passkey/login/begin");
  const asseResp = await startAuthentication({ optionsJSON });
  const { data } = await client.post("/affiliate/auth/passkey/login/finish", {
    credential: asseResp,
  });
  return data;
}

export async function listPasskeys() {
  const { data } = await client.get("/affiliate/auth/passkey/list");
  return data.passkeys || [];
}

export async function renamePasskey(credentialId, nickname) {
  const { data } = await client.patch(
    `/affiliate/auth/passkey/${encodeURIComponent(credentialId)}`,
    { nickname },
  );
  return data;
}

export async function removePasskey(credentialId) {
  const { data } = await client.delete(
    `/affiliate/auth/passkey/${encodeURIComponent(credentialId)}`,
  );
  return data;
}

// A few AAGUIDs we can pretty-print. Everything else falls back to "Passkey".
// This is display-only — never used as a security signal.
const AAGUID_LABELS = {
  "adce0002-35bc-c60a-648b-0b25f1f05503": "Chrome / Google Password Manager",
  "08987058-cadc-4b81-b6e1-30de50dcbe96": "Windows Hello",
  "9ddd1817-af5a-4672-a2b9-3e3dd95000a9": "Windows Hello",
  "6028b017-b1d4-4c02-b4b3-afcdafc96bb2": "Windows Hello",
  "dd4ec289-e01d-41c9-bb89-70fa845d4bf2": "iCloud Keychain",
  "fbfc3007-154e-4ecc-8c0b-6e020557d7bd": "iCloud Keychain",
  "531126d6-e717-415c-9320-3d9aa6981239": "Dashlane",
  "531126d6-e717-415c-9320-3d9aa6981238": "1Password",
  "b84e4048-15dc-4dd0-8640-f4f60813c8af": "1Password",
  "bada5566-a7aa-401f-bd96-45619a55120d": "1Password",
  "cc45f64e-52a2-451b-831a-4edd8022a202": "ToothPic",
};

export function friendlyAuthenticatorName(aaguid) {
  if (!aaguid) return null;
  return AAGUID_LABELS[String(aaguid).toLowerCase()] || null;
}
